<template>
    <div class="content-form">
        <b-card class="mb-2">
            <template #header>
                <div class="d-flex align-items-center">
                    <feather-icon icon="FileTextIcon" size="18" class="mr-50" />
                    <h5 class="mb-0">Temel Bilgiler</h5>
                </div>
            </template>

            <b-row>
                <b-col cols="12" md="6">
                    <b-form-group label="Content Key" label-for="contentKey" class="is-required">
                        <b-form-input id="contentKey" v-model="localValue.contentKey" placeholder="unique-content-key" :state="getValidationState('contentKey')" />
                        <b-form-invalid-feedback :state="getValidationState('contentKey')">
                            {{ errors.contentKey }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>

                <b-col cols="12" md="6">
                    <b-form-group label="Slug" label-for="slug" class="is-required">
                        <b-form-input id="slug" v-model="localValue.slug" placeholder="url-friendly-slug" :state="getValidationState('slug')" />
                        <b-form-invalid-feedback :state="getValidationState('slug')">
                            {{ errors.slug }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col cols="12" md="6">
                    <b-form-group label="Üst İçerik" label-for="parent">
                        <v-select-list
                            id="parent"
                            v-model="localValue.parentContentId"
                            :options="parentContents"
                            :reduce="(c) => c.Id"
                            :get-option-label="getParentContentLabel"
                            placeholder="Üst İçerik (Opsiyonel)"
                        />
                    </b-form-group>
                </b-col>

                <b-col cols="12" md="6">
                    <b-form-group label="Bağlı Navigasyonlar" label-for="navigations">
                        <v-select-list
                            id="navigations"
                            v-model="selectedNavigationIds"
                            :options="navigations"
                            :reduce="(nav) => nav.Id"
                            label="Title"
                            placeholder="Navigasyon Seçin..."
                            multiple
                            clearable
                            @input="onNavigationSelectionChange"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row v-if="localValue.navigationIds && localValue.navigationIds.length > 0">
                <b-col cols="12">
                    <div class="bg-light rounded p-1 mb-2 border">
                        <div class="d-flex align-items-center justify-content-between mb-1">
                            <small class="text-muted font-weight-bold text-uppercase">Seçili Navigasyonlar</small>
                            <b-badge variant="primary" pill>{{ localValue.navigationIds.length }}</b-badge>
                        </div>

                        <div v-for="navItem in localValue.navigationIds" :key="navItem.navigationId" class="d-flex align-items-center justify-content-between bg-white border rounded mb-1 shadow-sm">
                            <div class="d-flex align-items-center flex-grow-1">
                                <div class="cursor-pointer pl-1 pb-25 pr-50" @click="setPrimaryNavigation(navItem.navigationId)" v-b-tooltip.hover title="Ana Navigasyon Yap">
                                    <feather-icon icon="StarIcon" size="18" :class="navItem.isPrimary ? 'text-warning' : 'text-muted'" :fill="navItem.isPrimary ? 'currentColor' : 'none'" />
                                </div>

                                <span class="font-weight-bolder text-dark mr-2" style="font-size: 0.9rem">
                                    {{ getNavigationTitle(navItem.navigationId) }}
                                </span>

                                <b-badge variant="light-secondary" class="font-weight-normal" style="font-size: 0.75rem"> Sıra: {{ navItem.sortOrder }} </b-badge>
                            </div>

                            <div class="d-flex align-items-center mt-50">
                                <b-form-checkbox v-model="navItem.isActive" switch class="mr-2">
                                    <span style="font-size: 0.8rem">{{ navItem.isActive ? "Aktif" : "Pasif" }}</span>
                                </b-form-checkbox>

                                <div class="cursor-pointer text-danger pr-1 pb-75 hover-scale" @click="removeNavigation(navItem.navigationId)" v-b-tooltip.hover title="Listeden Çıkar">
                                    <feather-icon icon="XIcon" size="18" />
                                </div>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row>
                <b-col cols="12" md="4">
                    <b-form-group label="Yayın Tarihi" label-for="publishDate">
                        <v-date-picker id="publishDate" v-model="localValue.publishDate" placeholder="Tarih Seçin" class="w-100" />
                    </b-form-group>
                </b-col>

                <b-col cols="12" md="4">
                    <b-form-group label="Sıra" label-for="sortOrder">
                        <b-form-spinbutton id="sortOrder" v-model="localValue.sortOrder" min="0" />
                    </b-form-group>
                </b-col>

                <b-col cols="12" md="4">
                    <b-form-group label="Durum ve Özellikler" label-for="isActive">
                        <div id="isActive" class="d-flex align-items-center h-100 mt-50">
                            <b-form-checkbox v-model="localValue.isActive" switch class="mr-2">
                                {{ localValue.isActive ? "Aktif" : "Pasif" }}
                            </b-form-checkbox>
                            <b-form-checkbox v-model="localValue.isFeatured" switch>
                                {{ localValue.isFeatured ? "Öne Çıkan" : "Normal" }}
                            </b-form-checkbox>
                        </div>
                    </b-form-group>
                </b-col>
            </b-row>
        </b-card>

        <b-card class="mb-2">
            <template #header>
                <div class="d-flex align-items-center">
                    <feather-icon icon="GlobeIcon" size="18" class="mr-50" />
                    <h5 class="mb-0">Çeviriler</h5>
                </div>
            </template>

            <b-tabs pills>
                <b-tab v-for="lang in languages" :key="lang.LanguageCode" :title="lang.LanguageName">
                    <translation-form :value="getTranslation(lang.LanguageCode)" :language-code="lang.LanguageCode" @input="updateTranslation(lang.LanguageCode, $event)" />
                </b-tab>
            </b-tabs>
        </b-card>

        <b-card class="mb-2">
            <content-media-manager v-model="localValue.mediaFiles" @open-gallery="openGalleryModal" />

            <file-manager-modal
                v-if="galleryModalVisible"
                :modal-id="'content-gallery-selector'"
                :folder-name="currentGalleryFolder ? currentGalleryFolder.FolderName : 'Genel Galeri'"
                :folder-id="currentGalleryFolder ? currentGalleryFolder.Id : null"
                :folders="galleryFolders"
                :files="galleryFiles"
                :loading="galleryLoading"
                :selection-mode="true"
                @hidden="galleryModalVisible = false"
                @selection-confirmed="handleGallerySelection"
                @folder-change="onGalleryFolderChange"
            />
        </b-card>

        <b-card class="mb-2">
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <feather-icon icon="TagIcon" size="18" class="mr-50" />
                        <h5 class="mb-0">Meta Verileri</h5>
                    </div>
                </div>
            </template>

            <div class="mb-2">
                <b-button variant="primary" size="sm" @click="addMetaData"> <feather-icon icon="PlusIcon" size="14" class="mr-25" /> Meta Ekle </b-button>
            </div>

            <b-row v-if="localValue.metaData && localValue.metaData.length">
                <b-col v-for="(meta, index) in localValue.metaData" :key="index" cols="12" md="6" class="mb-1">
                    <div class="border rounded p-2 shadow-sm d-flex flex-column flex-md-row align-items-md-center justify-content-between position-relative hover-card h-100 theme-item">
                        <div class="d-flex flex-row flex-md-column align-items-center align-items-md-start mr-md-2 mb-2 mb-md-0" style="min-width: 100px">
                            <div class="mb-1">
                                <b-badge variant="light-info" class="mr-1">
                                    {{ meta.MetaGroup || "Genel" }}
                                </b-badge>
                                <b-badge v-if="meta.LanguageCode" variant="light-info">
                                    {{ meta.LanguageCode }}
                                </b-badge>
                            </div>
                            <div class="text-muted small d-flex align-items-center" style="font-size: 0.75rem">
                                <feather-icon icon="ListIcon" size="10" class="mr-50" />
                                <span
                                    >Sıra: <b>{{ meta.SortOrder }}</b></span
                                >
                            </div>
                        </div>

                        <div class="d-flex flex-column flex-md-row flex-grow-1 mr-md-1 border-left-md pl-md-2" style="min-width: 0">
                            <div class="mb-1 mb-md-0 mr-md-2" style="flex: 0 0 35%; min-width: 0">
                                <label class="d-block text-muted small font-weight-bold text-uppercase mb-0" style="font-size: 0.65rem"> Anahtar </label>
                                <div class="text-truncate font-weight-bolder text-dark small" v-b-tooltip.hover :title="meta.MetaKey">
                                    {{ meta.MetaKey }}
                                </div>
                            </div>

                            <div style="flex: 1; min-width: 0">
                                <label class="d-block text-muted small font-weight-bold text-uppercase mb-0" style="font-size: 0.65rem"> Değer </label>
                                <div class="text-truncate text-secondary small" v-b-tooltip.hover :title="meta.MetaValue">
                                    {{ meta.MetaValue || "-" }}
                                </div>
                            </div>
                        </div>

                        <div class="d-flex align-items-center justify-content-end mt-1 mt-md-0 action-buttons flex-shrink-0">
                            <b-button
                                variant="flat-primary"
                                size="sm"
                                class="btn-icon rounded-circle p-0 mr-50"
                                style="width: 24px; height: 24px"
                                v-b-tooltip.hover
                                title="Kopyala"
                                @click="cloneMetaData(meta)"
                            >
                                <feather-icon icon="CopyIcon" size="14" />
                            </b-button>
                            <b-button
                                variant="flat-secondary"
                                size="sm"
                                class="btn-icon rounded-circle p-0 mr-50"
                                style="width: 24px; height: 24px"
                                v-b-tooltip.hover
                                title="Düzenle"
                                @click="editMetaData(index)"
                            >
                                <feather-icon icon="Edit2Icon" size="14" />
                            </b-button>
                            <b-button
                                variant="flat-danger"
                                size="sm"
                                class="btn-icon rounded-circle p-0"
                                style="width: 24px; height: 24px"
                                v-b-tooltip.hover
                                title="Sil"
                                @click="removeMetaData(index)"
                            >
                                <feather-icon icon="TrashIcon" size="14" />
                            </b-button>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <div v-else class="border rounded p-4 text-center theme-item">
                <feather-icon icon="TagIcon" size="32" class="text-muted mb-1" />
                <p class="text-muted mb-0">Henüz meta verisi eklenmedi.</p>
            </div>
        </b-card>
        <meta-data-modal :modal-id="'meta-modal'" :item="currentMetaItem" :is-editing="editingMetaIndex !== -1" :languages="languages" :group-options="metaGroupOptions" @save="handleSaveMetaData" />
    </div>
</template>

<script>
import { BCard, BRow, BCol, BFormGroup, BFormInput, BFormInvalidFeedback, BFormCheckbox, BFormSpinbutton, BTabs, BTab, BButton, BAlert, BBadge, VBTooltip } from "bootstrap-vue";
import VSelectList from "@/components/VSelectList.vue";
import VDatePicker from "@/components/VDatePicker.vue";
import TranslationForm from "./TranslationForm.vue";
import MetaDataModal from "../modals/MetaDataModal.vue";
import ContentMediaManager from "./ContentMediaManager.vue";
import FileManagerModal from "@/views/website/modals/FileManagerModal.vue";
import WebsiteApi from "@/api/website";

export default {
    name: "ContentForm",
    components: {
        BCard,
        BRow,
        BCol,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,
        BFormCheckbox,
        BFormSpinbutton,
        BTabs,
        BTab,
        BButton,
        BAlert,
        BBadge,
        VSelectList,
        VDatePicker,
        TranslationForm,
        MetaDataModal,
        ContentMediaManager,
        FileManagerModal,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
        navigations: {
            type: Array,
            default: () => [],
        },
        languages: {
            type: Array,
            default: () => [],
        },
        parentContents: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedNavigationIds: [],
            mediaTypes: [
                { label: "Resim", value: "image" },
                { label: "Video", value: "video" },
                { label: "Thumbnail", value: "thumbnail" },
                { label: "Galeri", value: "gallery" },
            ],
            currentMetaItem: {},
            editingMetaIndex: -1,
            galleryModalVisible: false,
            galleryFiles: [],
            galleryFolders: [],
            galleryLoading: false,
            currentGalleryFolder: null,
        };
    },
    computed: {
        localValue: {
            get() {
                return this.value || this.getEmptyContent();
            },
            set(val) {
                this.$emit("input", val);
            },
        },
        metaGroupOptions() {
            if (!this.localValue.metaData) return [];
            const groups = [...new Set(this.localValue.metaData.map((m) => m.MetaGroup).filter((g) => g))];
            return groups.map((g) => ({ label: g, value: g }));
        },
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal) {
                    this.initializeTranslations();
                    this.initializeNavigationIds();
                }
            },
            immediate: true,
        },
        languages: {
            handler() {
                if (this.value) {
                    this.initializeTranslations();
                }
            },
            immediate: true,
        },
        "localValue.navigationIds": {
            deep: true,
            immediate: true,
            handler(newVal) {
                if (newVal && Array.isArray(newVal)) {
                    this.selectedNavigationIds = newVal.map((n) => n.navigationId);
                }
            },
        },
    },
    methods: {
        getEmptyContent() {
            return {
                navigationIds: [],
                parentContentId: null,
                contentKey: "",
                slug: "",
                publishDate: null,
                sortOrder: 0,
                isActive: true,
                isFeatured: false,
                translations: [],
                mediaFiles: [],
                metaData: [],
            };
        },

        initializeNavigationIds() {
            if (!this.localValue.navigationIds) {
                this.$set(this.localValue, "navigationIds", []);
            }
        },

        onNavigationSelectionChange(selectedIds) {
            if (!this.localValue.navigationIds) {
                this.$set(this.localValue, "navigationIds", []);
            }

            // Add new selections
            selectedIds.forEach((navId) => {
                if (!this.localValue.navigationIds.find((n) => n.navigationId === navId)) {
                    this.localValue.navigationIds.push({
                        navigationId: navId,
                        isPrimary: this.localValue.navigationIds.length === 0,
                        sortOrder: this.localValue.navigationIds.length,
                        isActive: true,
                    });
                }
            });

            // Remove deselected items
            this.localValue.navigationIds = this.localValue.navigationIds.filter((n) => selectedIds.includes(n.navigationId));
            this.updateNavigationSortOrders();
        },

        getNavigationTitle(navigationId) {
            const nav = this.navigations.find((n) => n.Id === navigationId);
            return nav ? nav.Title : "Bilinmeyen Navigasyon";
        },

        setPrimaryNavigation(navigationId) {
            // Remove primary from all
            this.localValue.navigationIds.forEach((n) => {
                n.isPrimary = false;
            });
            // Set new primary
            const target = this.localValue.navigationIds.find((n) => n.navigationId === navigationId);
            if (target) {
                target.isPrimary = true;
            }
        },

        removeNavigation(navigationId) {
            this.localValue.navigationIds = this.localValue.navigationIds.filter((n) => n.navigationId !== navigationId);
            this.selectedNavigationIds = this.selectedNavigationIds.filter((id) => id !== navigationId);
            this.updateNavigationSortOrders();
        },

        updateNavigationSortOrders() {
            if (this.localValue.navigationIds && this.localValue.navigationIds.length > 0) {
                this.localValue.navigationIds.forEach((nav, index) => {
                    nav.sortOrder = index;
                });
            }
        },

        initializeTranslations() {
            if (!this.localValue.translations) {
                this.$set(this.localValue, "translations", []);
            }

            this.languages.forEach((lang) => {
                if (!this.localValue.translations.find((t) => t.LanguageCode === lang.LanguageCode)) {
                    this.localValue.translations.push({
                        LanguageCode: lang.LanguageCode,
                        Title: "",
                        ShortTitle: "",
                        Summary: "",
                        Content: "",
                        Author: "",
                        SeoTitle: "",
                        SeoDescription: "",
                        SeoKeywords: "",
                        OgImage: "",
                        OgImageAlt: "",
                    });
                }
            });
        },

        getTranslation(languageCode) {
            let translation = this.localValue.translations?.find((t) => t.LanguageCode === languageCode);

            if (!translation) {
                translation = {
                    LanguageCode: languageCode,
                    Title: "",
                    ShortTitle: "",
                    Summary: "",
                    Content: "",
                    Author: "",
                    SeoTitle: "",
                    SeoDescription: "",
                    SeoKeywords: "",
                    OgImage: "",
                    OgImageAlt: "",
                };
            }

            return translation;
        },

        updateTranslation(languageCode, data) {
            const index = this.localValue.translations.findIndex((t) => t.LanguageCode === languageCode);

            if (index !== -1) {
                this.$set(this.localValue.translations, index, data);
            } else {
                this.localValue.translations.push(data);
            }
        },

        addMediaFile() {
            if (!this.localValue.mediaFiles) {
                this.$set(this.localValue, "mediaFiles", []);
            }

            this.localValue.mediaFiles.push({
                MediaType: "image",
                FilePath: "",
                Alt: "",
                Title: "",
                Description: "",
                FileSize: null,
                MimeType: "",
                Width: null,
                Height: null,
                SortOrder: this.localValue.mediaFiles.length,
                LanguageCode: null,
            });
        },

        removeMediaFile(index) {
            this.localValue.mediaFiles.splice(index, 1);
        },

        openMetaModal(item = null, index = -1) {
            this.editingMetaIndex = index;
            this.currentMetaItem = item
                ? { ...item }
                : {
                      MetaKey: "",
                      MetaValue: "",
                      LanguageCode: null,
                      MetaGroup: "",
                      SortOrder: this.localValue.metaData ? this.localValue.metaData.length : 0,
                  };
            this.$bvModal.show("meta-modal");
        },

        addMetaData() {
            this.openMetaModal();
        },

        editMetaData(index) {
            const item = this.localValue.metaData[index];
            this.openMetaModal(item, index);
        },

        cloneMetaData(sourceItem) {
            const newItem = {
                MetaKey: "",
                MetaValue: "",
                LanguageCode: sourceItem.LanguageCode,
                MetaGroup: sourceItem.MetaGroup,
                SortOrder: this.localValue.metaData.length,
            };
            this.openMetaModal(newItem, -1);
        },
        handleSaveMetaData(item) {
            if (!this.localValue.metaData) {
                this.$set(this.localValue, "metaData", []);
            }

            if (this.editingMetaIndex > -1) {
                this.$set(this.localValue.metaData, this.editingMetaIndex, item);
            } else {
                this.localValue.metaData.push(item);
            }
        },
        removeMetaData(index) {
            this.localValue.metaData.splice(index, 1);
        },

        getValidationState(field) {
            if (!this.errors || !field) return null;
            return this.errors[field] ? false : null;
        },
        getParentContentLabel(content) {
            if (!content || !content.Translations || !content.Translations.length) {
                return content.ContentKey || "Başlıksız İçerik";
            }
            const trTranslation = content.Translations.find((t) => t.LanguageCode === "tr");
            if (trTranslation && trTranslation.Title) {
                return trTranslation.Title;
            }
            return content.Translations[0].Title || content.ContentKey || "Başlıksız İçerik";
        },
        openGalleryModal() {
            this.galleryModalVisible = true;
            this.galleryLoading = true;
            this.galleryFiles = [];
            this.galleryFolders = [];

            WebsiteApi.Gallery.GetFoldersAdmin({ Take: 100 })
                .then((res) => {
                    if (res.data.Success && res.data.Data) {
                        this.galleryFolders = res.data.Data;

                        if (this.galleryFolders.length > 0) {
                            this.currentGalleryFolder = this.galleryFolders[0];
                            this.loadGalleryFiles(this.currentGalleryFolder.Id);
                        } else {
                            this.galleryLoading = false;
                        }
                    }
                })
                .catch((err) => {
                    this.galleryLoading = false;
                    console.error("Galeri klasörleri yüklenemedi", err);
                });
        },

        onGalleryFolderChange(folderId) {
            const folder = this.galleryFolders.find((f) => f.Id === folderId);
            if (folder) {
                this.currentGalleryFolder = folder;
                this.loadGalleryFiles(folderId);
            }
        },
        loadGalleryFiles(folderId) {
            WebsiteApi.Gallery.GetFilesAdmin({ VirtualFolderId: folderId, Take: 100 })
                .then((res) => {
                    if (res.data.Success) {
                        this.galleryFiles = res.data.Data || [];
                    }
                })
                .finally(() => {
                    this.galleryLoading = false;
                    this.$nextTick(() => {
                        this.$bvModal.show("content-gallery-selector");
                    });
                });
        },

        handleGallerySelection(selectedFiles) {
            if (!this.localValue.mediaFiles) {
                this.$set(this.localValue, "mediaFiles", []);
            }

            const startingOrder = this.localValue.mediaFiles.length;

            selectedFiles.forEach((file, index) => {
                const newMedia = {
                    MediaType: file.FileType === "video" ? "video" : "image", // Basit mapping
                    FilePath: file.FilePath,
                    Title: file.Title || file.FileName,
                    Alt: file.Alt || file.Title,
                    Description: file.Description,
                    Width: file.Width,
                    Height: file.Height,
                    FileSize: file.FileSize,
                    MimeType: file.MimeType,
                    SortOrder: startingOrder + index,
                    LanguageCode: this.localValue.translations?.[0]?.LanguageCode || "tr",
                };

                this.localValue.mediaFiles.push(newMedia);
            });
        },
    },
};
</script>

<style scoped>
.is-required >>> label::after,
.is-required /deep/ label::after {
    content: " *";
    color: #dc3545;
    font-weight: 700;
}
.hover-card {
    transition: all 0.2s ease;
    border: 1px solid #e2e2e2 !important;
}
.hover-card:hover {
    border-color: #7367f0 !important;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05) !important;
}
@media (min-width: 768px) {
    .border-left-md {
        border-left: 1px solid #ebe9f1;
    }
}
.theme-item {
    background-color: #ffffff;
    transition: all 0.3s ease;
}

.dark-layout .theme-item {
    background-color: #161d31 !important;
    border-color: #3b4253 !important;
}

.dark-layout .theme-item .text-dark {
    color: #d0d2d6 !important;
}
.dark-layout .theme-item .text-muted {
    color: #676d7d !important;
}
.dark-layout .theme-item .text-secondary {
    color: #b4b7bd !important;
}
.dark-layout .hover-card:hover {
    background-color: #202943 !important;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.6) !important;
    border-color: #7367f0 !important;
}

.dark-layout .hover-bg-light:hover {
    background-color: #3b4253 !important;
    color: #d0d2d6 !important;
}

.selected-navigations-container {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 8px;
}

.selected-navigations-container h6 {
    font-weight: 600;
    color: #495057;
    display: flex;
    align-items: center;
}

.navigations-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.navigation-item-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
}

.navigation-item-card:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.navigation-item-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.navigation-title {
    font-weight: 500;
    color: #1f2937;
    font-size: 14px;
}

.navigation-item-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

::v-deep .btn-icon {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
